import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../api';
import { runsApi, type RunDetail, type RunToolCall } from '../api/runs';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link, navigate } from '../router';
import { errorText } from './Runs';
import { ACTION_STATUS_KEY, APPROVAL_DECISION_KEY, APPROVAL_KIND_KEY, ARTIFACT_STATUS_KEY, RUN_STATUS_KEY, actionStatusClass, asText, contextOf, decisionClass, fmtCost, fmtDuration, runStatusClass } from './runs.format';
import { fmtWhen } from './tasks.format';
import './runs.css';

const num = (n: number) => n.toLocaleString('uk-UA');
const version = (n: number | null | undefined) => (n === null || n === undefined ? '—' : t('runs.version.n', { n }));

/**
 * Прогін: the exact viewer of one Execution (ADR-0004 decision 8) — the versions it was pinned to,
 * what it cost, every turn and tool call from `ExecutionProvenance`, the stored context, the
 * artifacts it produced and the approvals it went through. Everything the agent wrote is data:
 * arguments, results and the context snapshot are rendered inside `<pre>`, never as markup.
 */
export function RunPage({ id, canManage, canStart }: { id: string; canManage: boolean; canStart: boolean }) {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () =>
    runsApi
      .get(id)
      .then((r) => {
        setRun(r);
        setNotFound(false);
      })
      .catch((e: Error) => {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(errorText(e));
      });

  useEffect(() => {
    setRun(null);
    setError(null);
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actionById = useMemo(() => new Map((run?.externalActions ?? []).map((a) => [a.id, a])), [run]);
  const context = contextOf(run?.provenance.contextSnapshot);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (notFound) return <p className="muted">{t('runs.notFound')}</p>;
  if (!run) return <p className="muted">{error ?? t('common.loading')}</p>;

  const cancel = async () => {
    if (!window.confirm(t('runs.action.cancelConfirm'))) return;
    if (await act(() => runsApi.cancel(run.id))) await reload();
  };
  const replay = async () => {
    const r = (await act(() => runsApi.replay(run.id))) as { executionId: string } | null;
    if (r) navigate(`/runs/${r.executionId}`);
  };

  const artifactColumns: Column<RunDetail['artifacts'][number]>[] = [
    { key: 'type', title: t('runs.artifacts.type'), render: (a) => a.type },
    { key: 'version', title: t('runs.artifacts.version'), render: (a) => t('runs.version.n', { n: a.version }) },
    { key: 'status', title: t('runs.artifacts.status'), render: (a) => <span className={a.status === 'approved' ? 'badge ok' : a.status === 'superseded' ? 'badge danger' : 'badge warn'}>{t(ARTIFACT_STATUS_KEY[a.status])}</span> },
    { key: 'createdAt', title: t('runs.artifacts.created'), render: (a) => <span className="muted">{fmtWhen(a.createdAt)}</span> },
  ];

  const actionColumns: Column<RunDetail['externalActions'][number]>[] = [
    { key: 'tool', title: t('runs.actions.tool'), render: (a) => <span className="mono">{a.toolRef}</span>, wrap: true },
    { key: 'status', title: t('runs.actions.status'), render: (a) => <span className={actionStatusClass(a.status)}>{t(ACTION_STATUS_KEY[a.status])}</span> },
    { key: 'attempt', title: t('runs.actions.attempt'), render: (a) => <span className="num">{a.attempt}</span> },
    { key: 'key', title: t('runs.actions.idempotency'), render: (a) => <span className="mono">{a.idempotencyKey.slice(0, 8)}…{a.idempotencyKey.slice(-4)}</span> },
    { key: 'reference', title: t('runs.actions.reference'), render: (a) => <span className="mono">{a.externalReference ?? '—'}</span> },
    { key: 'error', title: t('runs.actions.error'), render: (a) => (a.error ? <span className="error">{a.error}</span> : <span className="muted">—</span>), wrap: true },
  ];

  const approvalColumns: Column<RunDetail['approvals'][number]>[] = [
    { key: 'when', title: t('runs.approvals.when'), render: (a) => <span className="muted">{fmtWhen(a.requestedAt)}</span> },
    { key: 'kind', title: t('runs.approvals.kind'), render: (a) => t(APPROVAL_KIND_KEY[a.kind]) },
    { key: 'decision', title: t('runs.approvals.decision'), render: (a) => <span className={decisionClass(a.decision)}>{a.decision ? t(APPROVAL_DECISION_KEY[a.decision]) : t('runs.decision.pending')}</span> },
    { key: 'approver', title: t('runs.approvals.approver'), render: (a) => a.approver ?? <span className="muted">—</span> },
    { key: 'channel', title: t('runs.approvals.channel'), render: (a) => (a.channel === 'telegram' ? t('runs.channel.telegram') : a.channel === 'panel' ? t('runs.channel.panel') : <span className="muted">—</span>) },
    { key: 'expires', title: t('runs.approvals.expires'), render: (a) => <span className="muted">{a.decidedAt ? fmtWhen(a.decidedAt) : fmtWhen(a.expiresAt)}</span> },
  ];

  return (
    <div className="run">
      <div className="muted">
        <Link to="/runs">{t('runs.title')}</Link> ›{' '}
        {run.task ? <Link to={`/tasks/${run.task.id}`}>{run.task.key}</Link> : t('runs.detail.noTask')} · {run.agent.name}
      </div>
      <div className="head">
        <h1>{t('runs.detail.title', { task: run.task?.key ?? t('runs.detail.noTask'), agent: run.agent.name })}</h1>
        <div className="actions">
          <span className={runStatusClass(run.status)}>{t(RUN_STATUS_KEY[run.status])}</span>
          {canStart && run.task && (
            <button type="button" className="btn secondary small" onClick={() => void replay()} disabled={busy}>{t('runs.action.replay')}</button>
          )}
          {canManage && run.canCancel && (
            <button type="button" className="btn secondary small" onClick={() => void cancel()} disabled={busy}>{t('runs.action.cancel')}</button>
          )}
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      {run.error && <div className="note" style={{ marginBottom: 'var(--sp-3)' }}>{t('runs.detail.error')}: {run.error}</div>}

      <div className="card stack">
        <div className="versions">
          <div><span className="muted">{t('runs.versions.template')}</span><strong>{version(run.templateVersion)}</strong></div>
          <div><span className="muted">{t('runs.versions.instance')}</span><strong>{version(run.agentInstanceVersion)}</strong></div>
          <div><span className="muted">{t('runs.versions.policy')}</span><strong>{version(run.policyVersion)}</strong></div>
          <div><span className="muted">{t('runs.versions.prompt')}</span><strong>{run.promptVersion}</strong></div>
          <div><span className="muted">{t('runs.versions.model')}</span><strong>{run.model}</strong></div>
          <div><span className="muted">{t('runs.versions.attempt')}</span><strong>{run.attempt}</strong></div>
        </div>
        <div className="muted">
          {t('runs.tokens', { input: num(run.inputTokens), output: num(run.outputTokens), cached: num(run.cachedTokens) })} · {t('runs.cost')}:{' '}
          <strong style={{ color: 'var(--c-text)' }}>{fmtCost(run.cost)} {run.currency}</strong> · {t('runs.col.duration')}: {fmtDuration(run.durationSec)} · {t('runs.col.turns')}: {run.turns}
        </div>
      </div>

      <h2>{t('runs.timeline.title')}</h2>
      <div className="card">
        <Timeline run={run} actionById={actionById} />
      </div>

      <h2>{t('runs.context.title')}</h2>
      <div className="card stack">
        {context === null ? (
          <span className="muted">{t('runs.context.empty')}</span>
        ) : (
          <>
            <Block summary={t('runs.context.system')} text={context.system} />
            <Block summary={t('runs.context.user')} text={context.user} />
          </>
        )}
        {run.provenance.finalDecision !== null && <Block summary={t('runs.result.title')} text={asText(run.provenance.finalDecision)} />}
      </div>

      <h2>{t('runs.artifacts.title')}</h2>
      <Table columns={artifactColumns} rows={run.artifacts} rowKey={(a) => a.id} empty={t('runs.artifacts.empty')} />

      <h2>{t('runs.actions.title')}</h2>
      <Table columns={actionColumns} rows={run.externalActions} rowKey={(a) => a.id} empty={t('runs.actions.empty')} />

      <h2>{t('runs.approvals.title')}</h2>
      <Table columns={approvalColumns} rows={run.approvals} rowKey={(a) => a.id} empty={t('runs.approvals.empty')} />
    </div>
  );
}

/** A collapsed `<pre>`: everything the model or a tool produced is text, never markup. */
function Block({ summary, text, open }: { summary: string; text: string; open?: boolean }) {
  if (!text) return null;
  return (
    <details className="details-block" open={open}>
      <summary>{summary}</summary>
      <pre className="codeblock">{text}</pre>
    </details>
  );
}

/**
 * The run timeline: the turns the engine recorded and, under each of them, the tool calls of that
 * turn with their arguments, their result and the ExternalAction the side effect went through.
 * `ExecutionProvenance.toolCalls` is the only per-step record the engine keeps, so a turn with no
 * tool call shows up only as the final line.
 */
function Timeline({ run, actionById }: { run: RunDetail; actionById: Map<string, RunDetail['externalActions'][number]> }) {
  const calls = run.provenance.toolCalls;
  const turns = [...new Set(calls.map((c) => c.turn))].sort((a, b) => a - b);
  if (calls.length === 0 && run.turns === 0) return <p className="muted">{t('runs.timeline.empty')}</p>;

  const rows: ReactNode[] = [];
  for (const turn of turns) {
    const ofTurn = calls.filter((c) => c.turn === turn);
    rows.push(
      <li key={`turn-${turn}`}>
        <span className="muted">{fmtWhen(ofTurn[0]?.at ?? run.startedAt)}</span>
        <span className="what">{t('runs.timeline.turn', { n: turn, model: run.model })}</span>
      </li>,
    );
    for (const c of ofTurn) rows.push(<ToolCall key={`${turn}-${c.callId}`} call={c} action={c.externalActionId ? actionById.get(c.externalActionId) : undefined} />);
  }
  rows.push(
    <li key="final">
      <span className="muted">{fmtWhen(run.finishedAt ?? run.updatedAt)}</span>
      <span className="what">
        {t(RUN_STATUS_KEY[run.status])} · {t('runs.col.turns')}: {run.turns} · {fmtCost(run.cost)} {run.currency}
      </span>
    </li>,
  );
  return <ul className="timeline">{rows}</ul>;
}

function ToolCall({ call, action }: { call: RunToolCall; action?: RunDetail['externalActions'][number] }) {
  const denied = call.verdict !== null && call.verdict !== 'allow';
  return (
    <li>
      <span className="muted">{fmtWhen(call.at)}</span>
      <span className="what">
        <details className="details-block">
          <summary>
            {t('runs.timeline.tool', { tool: call.toolName })}
            {denied && <> · <span className="badge danger">{t('runs.timeline.denied', { reason: call.reason ?? call.verdict ?? '' })}</span></>}
            {call.isError && !denied && <> · <span className="badge danger">{t('runs.timeline.error')}</span></>}
          </summary>
          <div className="stack">
            <span className="muted">{t('runs.timeline.args')}</span>
            <pre className="codeblock">{asText(call.args)}</pre>
            {call.content !== '' && (
              <>
                <span className="muted">{t('runs.timeline.result')}</span>
                <pre className="codeblock">{call.content}</pre>
              </>
            )}
            {call.resumed && <span className="muted">{t('runs.timeline.resumed', { how: call.resumed })}</span>}
            {action && (
              <div className="facts">
                <div>
                  <span className="muted">{t('runs.actions.idempotency')}</span>
                  <span className="mono">{action.idempotencyKey.slice(0, 8)}…{action.idempotencyKey.slice(-4)}</span>
                </div>
                <div>
                  <span className="muted">{t('runs.actions.status')}</span>
                  <span className={actionStatusClass(action.status)}>{t(ACTION_STATUS_KEY[action.status])}</span>
                </div>
                <div>
                  <span className="muted">{t('runs.actions.reference')}</span>
                  <span className="mono">{action.externalReference ?? '—'}</span>
                </div>
              </div>
            )}
          </div>
        </details>
      </span>
    </li>
  );
}
